/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import CheckNums from '../../../core/checks/numbers/CheckNums.js';
import { OptionalField } from '../../../core/decorator/OptionalDecorator.js';
import { RESERVE_DECIMALS } from '../../../domain/context/reserve/Reserve.js';
import BigDecimal from '../../../domain/context/shared/BigDecimal.js';
import InvalidDecimalRange from '../../../domain/context/stablecoin/error/InvalidDecimalRange.js';
import { StableCoin } from '../../../domain/context/stablecoin/StableCoin.js';
import { TokenSupplyType } from '../../../domain/context/stablecoin/TokenSupply.js';
import { InvalidType } from './error/InvalidType.js';
import ValidatedRequest from './validation/ValidatedRequest.js';
import Validation from './validation/Validation.js';
export default class CreateRequest extends ValidatedRequest {
    get decimals() {
        return this._decimals;
    }
    set decimals(value) {
        this._decimals = typeof value === 'number' ? value : parseFloat(value);
    }
    constructor({ name, symbol, decimals, initialSupply, maxSupply, freezeDefault, freezeKey, kycKey, wipeKey, pauseKey, supplyType, feeScheduleKey, stableCoinFactory, hederaTokenManager, reserveAddress, reserveInitialAmount, createReserve, grantKYCToOriginalSender, burnRoleAccount, wipeRoleAccount, rescueRoleAccount, pauseRoleAccount, freezeRoleAccount, deleteRoleAccount, kycRoleAccount, cashInRoleAccount, cashInRoleAllowance, metadata, proxyAdminOwnerAccount, }) {
        super({
            name: (val) => {
                return StableCoin.checkName(val);
            },
            symbol: (val) => {
                return StableCoin.checkSymbol(val);
            },
            decimals: (val) => {
                return StableCoin.checkInteger(val);
            },
            initialSupply: (val) => {
                if (val === undefined || val === '') {
                    return;
                }
                if (!BigDecimal.isBigDecimal(val)) {
                    return [new InvalidType(val)];
                }
                if (CheckNums.hasMoreDecimals(val, this.decimals)) {
                    return [new InvalidDecimalRange(val, this.decimals)];
                }
                const bInitialSupply = BigDecimal.fromString(val, this.decimals);
                const bMaxSupply = this.maxSupply &&
                    BigDecimal.isBigDecimal(this.maxSupply) &&
                    !CheckNums.hasMoreDecimals(this.maxSupply, this.decimals)
                    ? BigDecimal.fromString(this.maxSupply, this.decimals)
                    : undefined;
                return StableCoin.checkInitialSupply(bInitialSupply, this.decimals, bMaxSupply);
            },
            maxSupply: (val) => {
                if (val === undefined) {
                    return;
                }
                if (!BigDecimal.isBigDecimal(val)) {
                    return [new InvalidType(val)];
                }
                if (CheckNums.hasMoreDecimals(val, this.decimals)) {
                    return [new InvalidDecimalRange(val, this.decimals)];
                }
                const bMaxSupply = BigDecimal.fromString(val, this.decimals);
                const bInitialSupply = this.initialSupply &&
                    BigDecimal.isBigDecimal(this.initialSupply) &&
                    !CheckNums.hasMoreDecimals(this.initialSupply, this.decimals)
                    ? BigDecimal.fromString(this.initialSupply, this.decimals)
                    : undefined;
                return StableCoin.checkMaxSupply(bMaxSupply, this.decimals, bInitialSupply, this.supplyType);
            },
            freezeKey: Validation.checkPublicKey(),
            kycKey: Validation.checkPublicKey(),
            wipeKey: Validation.checkPublicKey(),
            pauseKey: Validation.checkPublicKey(),
            feeScheduleKey: Validation.checkPublicKey(),
            stableCoinFactory: Validation.checkContractId(),
            hederaTokenManager: Validation.checkContractId(),
            reserveAddress: Validation.checkContractId(),
            reserveInitialAmount: (val) => {
                if (val === undefined ||
                    val === '' ||
                    this.createReserve == false) {
                    return;
                }
                if (!BigDecimal.isBigDecimal(val)) {
                    return [new InvalidType(val)];
                }
                if (CheckNums.hasMoreDecimals(val, RESERVE_DECIMALS)) {
                    return [new InvalidDecimalRange(val, RESERVE_DECIMALS)];
                }
                const commonDecimals = RESERVE_DECIMALS > this.decimals
                    ? RESERVE_DECIMALS
                    : this.decimals;
                const reserveInitialAmount = BigDecimal.fromString(val, commonDecimals);
                const bInitialSupply = this.initialSupply &&
                    BigDecimal.isBigDecimal(this.initialSupply) &&
                    !CheckNums.hasMoreDecimals(this.initialSupply, this.decimals)
                    ? BigDecimal.fromString(this.initialSupply, commonDecimals)
                    : undefined;
                return StableCoin.checkReserveInitialAmount(reserveInitialAmount, commonDecimals, bInitialSupply);
            },
            burnRoleAccount: Validation.checkHederaIdFormat(true),
            wipeRoleAccount: Validation.checkHederaIdFormat(true),
            rescueRoleAccount: Validation.checkHederaIdFormat(true),
            pauseRoleAccount: Validation.checkHederaIdFormat(true),
            freezeRoleAccount: Validation.checkHederaIdFormat(true),
            deleteRoleAccount: Validation.checkHederaIdFormat(true),
            kycRoleAccount: Validation.checkHederaIdFormat(true),
            cashInRoleAccount: Validation.checkHederaIdFormat(true),
            cashInRoleAllowance: (val) => {
                if (val === undefined || val === '') {
                    return;
                }
                if (!BigDecimal.isBigDecimal(val)) {
                    return [new InvalidType(val)];
                }
                if (CheckNums.hasMoreDecimals(val, this.decimals)) {
                    return [new InvalidDecimalRange(val, this.decimals)];
                }
                return StableCoin.checkCashInAllowance(BigDecimal.fromString(val, this.decimals), this.decimals);
            },
            metadata: Validation.checkString({ max: 100, emptyCheck: false }),
            proxyAdminOwnerAccount: Validation.checkContractId(),
        });
        this.name = name;
        this.symbol = symbol;
        this.decimals =
            typeof decimals === 'number' ? decimals : parseInt(decimals);
        this.initialSupply = initialSupply;
        this.maxSupply = maxSupply;
        this.freezeDefault = freezeDefault;
        this.freezeKey = freezeKey;
        this.kycKey = kycKey;
        this.wipeKey = wipeKey;
        this.pauseKey = pauseKey;
        this.feeScheduleKey = feeScheduleKey;
        this.supplyType = supplyType;
        this.stableCoinFactory = stableCoinFactory;
        this.hederaTokenManager = hederaTokenManager;
        this.reserveAddress = reserveAddress;
        this.reserveInitialAmount = reserveInitialAmount;
        this.createReserve = createReserve;
        this.grantKYCToOriginalSender = grantKYCToOriginalSender;
        this.burnRoleAccount = burnRoleAccount;
        this.wipeRoleAccount = wipeRoleAccount;
        this.rescueRoleAccount = rescueRoleAccount;
        this.pauseRoleAccount = pauseRoleAccount;
        this.freezeRoleAccount = freezeRoleAccount;
        this.deleteRoleAccount = deleteRoleAccount;
        this.kycRoleAccount = kycRoleAccount;
        this.cashInRoleAccount = cashInRoleAccount;
        this.cashInRoleAllowance = cashInRoleAllowance;
        this.metadata = metadata;
        this.proxyAdminOwnerAccount = proxyAdminOwnerAccount;
    }
}
__decorate([
    OptionalField(),
    __metadata("design:type", String)
], CreateRequest.prototype, "stableCoinFactory", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", String)
], CreateRequest.prototype, "hederaTokenManager", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", String)
], CreateRequest.prototype, "reserveAddress", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "reserveInitialAmount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "initialSupply", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "maxSupply", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Boolean)
], CreateRequest.prototype, "freezeDefault", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "freezeKey", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "kycKey", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "wipeKey", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "pauseKey", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "feeScheduleKey", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", String)
], CreateRequest.prototype, "supplyType", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Boolean)
], CreateRequest.prototype, "grantKYCToOriginalSender", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "burnRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "wipeRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "rescueRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "pauseRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "freezeRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "deleteRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "kycRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "cashInRoleAccount", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "cashInRoleAllowance", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", Object)
], CreateRequest.prototype, "metadata", void 0);
__decorate([
    OptionalField(),
    __metadata("design:type", String)
], CreateRequest.prototype, "proxyAdminOwnerAccount", void 0);
